import React, { useState, useEffect } from 'react';
import { loadCaptchaEnginge, LoadCanvasTemplate, validateCaptcha } from 'react-simple-captcha';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    Container,
    Box,
    Paper,
    TextField,
    Button,
    Typography,
    CircularProgress,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    InputAdornment,
    Slide,
    Zoom,
    Fade,
    Grid,
    MenuItem,
    ListItemText,
} from '@mui/material';
import {
    Visibility,
    VisibilityOff,
    Lock as LockIcon,
    Person as PersonIcon,
    ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { login, clearError } from '../store/slices/authSlice';
import { AppDispatch, RootState } from '../store';
import apiService from '../services/api.service';
import ResponsiveDialog from '../components/common/ResponsiveDialog';
import { usePrefersReducedMotion } from '../hooks/useBreakpoints';
import { ServerApp, appAddress, otherServerApps } from '../utils/serverApps';

// The application picker's own entry: this page signs in to PRPO.
const PRPO_APP = 'prpo';
const PRPO_APP_NAME = 'SAP ProcureFlow (PRPO)';

interface FieldError {
    username?: string;
    password?: string;
    captcha?: string;
}

const Login: React.FC = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const reduceMotion = usePrefersReducedMotion();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, isLoading, error } = useSelector((state: RootState) => state.auth);
    const [loginResult, setLoginResult] = useState<any>(null);
    const [captchaInput, setCaptchaInput] = useState('');
    const [fieldErrors, setFieldErrors] = useState<FieldError>({});
    const [showPasswordChangeDialog, setShowPasswordChangeDialog] = useState(false);
    const [passwordChangeData, setPasswordChangeData] = useState<{ userId: number; username: string } | null>(null);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordChangeError, setPasswordChangeError] = useState<string | null>(null);
    const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
    // The other applications this server hosts (DEV); none on prod and KSA, which hides the picker.
    const [serverApps, setServerApps] = useState<ServerApp[]>([]);
    const [appChoice, setAppChoice] = useState(PRPO_APP);
    const chosenApp = serverApps.find((app) => app.id === appChoice) || null;

    useEffect(() => {
        let cancelled = false;
        apiService
            .getServerApps()
            .then((res) => {
                if (!cancelled) setServerApps(otherServerApps(res?.data?.apps, window.location.origin));
            })
            .catch(() => {
                // No list (an older backend, or the request failed): PRPO only, as before.
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (isAuthenticated) {
            const params = new URLSearchParams(location.search);
            const ret = params.get('return');
            if (ret) {
                navigate(ret);
            } else {
                navigate('/dashboard');
            }
        }
    }, [isAuthenticated, navigate, location.search]);

    useEffect(() => {
        loadCaptchaEnginge(6);
    }, []);

    useEffect(() => {
        return () => {
            dispatch(clearError());
        };
    }, [dispatch]);

    useEffect(() => {
        if (error) {
            const err = String(error).toLowerCase();
            if (err === 'invalid_credentials') {
                setFieldErrors({ password: 'Wrong user ID or password. Please try again. Login ID is case sensitive.' });
            } else if (err.includes('captcha')) {
                setFieldErrors({ captcha: err });
            } else if (err.includes('too many')) {
                setFieldErrors({ password: String(error) });
            } else {
                setFieldErrors({ password: 'Unable to sign in. Please try again.' });
            }
        }
    }, [error]);

    useEffect(() => {
        if (loginResult?.payload?.requirePasswordChange) {
            setPasswordChangeData({
                userId: loginResult.payload.passwordChangeData.userId,
                username: loginResult.payload.passwordChangeData.username
            });
            setShowPasswordChangeDialog(true);
            setLoginResult(null);
        }
    }, [loginResult]);

    useEffect(() => {
        if (password) {
            setPasswordChangeData({ userId: 0, username: username.trim() });
        }
    }, [error, password, username]);

    const validateForm = (): boolean => {
        const errors: FieldError = {};
        let isValid = true;

        if (!username.trim()) {
            errors.username = 'Username is required';
            isValid = false;
        } else if (username.trim().length < 3) {
            errors.username = 'Username must be at least 3 characters';
            isValid = false;
        }

        if (!password) {
            errors.password = 'Password is required';
            isValid = false;
        }

        if (!captchaInput.trim()) {
            errors.captcha = 'Captcha is required';
            isValid = false;
        } else if (!validateCaptcha(captchaInput)) {
            errors.captcha = 'Captcha does not match';
            isValid = false;
        }

        setFieldErrors(errors);
        return isValid;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (chosenApp) {
            // Another application on this server: its own page signs the user in.
            window.location.assign(chosenApp.url);
            return;
        }
        setFieldErrors({});

        if (!validateForm()) {
            return;
        }

        const result = await dispatch(login({ username: username.trim(), password } as any));
        setLoginResult(result);
    };

    const handlePasswordChange = async () => {
        setPasswordChangeError(null);
        const errors: any = {};

        if (!newPassword || !confirmPassword) {
            setPasswordChangeError('Please fill in all fields');
            return;
        }

        if (newPassword.length < 8) {
            errors.newPassword = 'Password must be at least 8 characters long';
        }

        if (!/[A-Z]/.test(newPassword)) {
            errors.newPassword = 'Password must contain at least one uppercase letter';
        }

        if (!/[a-z]/.test(newPassword)) {
            errors.newPassword = 'Password must contain at least one lowercase letter';
        }

        if (!/[0-9]/.test(newPassword)) {
            errors.newPassword = 'Password must contain at least one number';
        }

        if (newPassword !== confirmPassword) {
            errors.confirmPassword = 'Passwords do not match';
        }

        if (newPassword === password) {
            errors.newPassword = 'New password must be different from current password';
        }

        if (Object.keys(errors).length > 0) {
            setPasswordChangeError(String(Object.values(errors)[0]));
            return;
        }

        setPasswordChangeLoading(true);
        try {
            const res = await apiService.firstTimePasswordChange(
                passwordChangeData!.userId,
                password,
                newPassword
            );

            if (res?.success && res?.data?.token) {
                localStorage.setItem('token', res.data.token);
                setShowPasswordChangeDialog(false);
                window.location.href = '/dashboard';
            } else {
                setPasswordChangeError(res?.message || 'Failed to change password');
            }
        } catch (err: any) {
            setPasswordChangeError(err.response?.data?.message || 'Failed to change password');
        } finally {
            setPasswordChangeLoading(false);
        }
    };

    const handleUsernameChange = (value: string) => {
        setUsername(value);
        if (fieldErrors.username) {
            setFieldErrors(prev => ({ ...prev, username: undefined }));
        }
        if (fieldErrors.password) {
            setFieldErrors(prev => ({ ...prev, password: undefined }));
        }
    };

    const handlePasswordChangeField = (value: string) => {
        setPassword(value);
        if (fieldErrors.password) {
            setFieldErrors(prev => ({ ...prev, password: undefined }));
        }
    };

    const handleCaptchaChange = (value: string) => {
        setCaptchaInput(value);
        if (fieldErrors.captcha) {
            setFieldErrors(prev => ({ ...prev, captcha: undefined }));
        }
    };

    const cardInner = (
        <Paper
            elevation={4}
            component="section"
            aria-labelledby="login-heading"
            sx={{
                p: { xs: 2.5, sm: 3.5 },
                borderRadius: 2,
                backgroundColor: 'rgba(255,255,255,0.98)',
                boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                border: '1px solid rgba(10, 110, 209, 0.1)',
            }}
        >
            <Box sx={{ textAlign: 'center', mb: 2.5 }}>
                <Box
                    aria-hidden="true"
                    sx={{
                        width: 56,
                        height: 56,
                        mx: 'auto',
                        mb: 1.5,
                        borderRadius: '12px',
                        backgroundColor: '#0a6ed1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <LockIcon sx={{ fontSize: 32, color: '#ffffff' }} />
                </Box>
                <Typography
                    component="h1"
                    id="login-heading"
                    variant="h5"
                    sx={{
                        fontWeight: 600,
                        color: '#0a6ed1',
                        mb: 0.5,
                        letterSpacing: '-0.02em',
                    }}
                >
                    SAP ProcureFlow
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 400 }}
                >
                    Sign in to continue
                </Typography>
            </Box>

                            <Box component="form" onSubmit={handleSubmit} aria-describedby="login-heading" noValidate>
                                {serverApps.length > 0 && (
                                    <TextField
                                        select
                                        label="Application"
                                        variant="outlined"
                                        fullWidth
                                        size="small"
                                        margin="dense"
                                        value={appChoice}
                                        onChange={(e) => setAppChoice(e.target.value)}
                                        disabled={isLoading}
                                        helperText={
                                            chosenApp
                                                ? `Opens ${appAddress(chosenApp.url)}, where you sign in to ${chosenApp.name}.`
                                                : undefined
                                        }
                                        SelectProps={{
                                            renderValue: (value) =>
                                                serverApps.find((app) => app.id === value)?.name || PRPO_APP_NAME,
                                        }}
                                    >
                                        <MenuItem value={PRPO_APP}>
                                            <ListItemText primary={PRPO_APP_NAME} secondary={window.location.host} />
                                        </MenuItem>
                                        {serverApps.map((app) => (
                                            <MenuItem key={app.id} value={app.id} disabled={!app.available}>
                                                <ListItemText
                                                    primary={app.name}
                                                    secondary={
                                                        app.available
                                                            ? appAddress(app.url)
                                                            : `${appAddress(app.url)} · not responding`
                                                    }
                                                />
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}

                                {/* PRPO's own sign-in; another application signs the user in on its own page. */}
                                <Box sx={{ display: chosenApp ? 'none' : 'block' }}>
                                    <TextField
                                        label="Username"
                                        variant="outlined"
                                        fullWidth
                                        size="small"
                                        margin="dense"
                                        value={username}
                                        onChange={(e) => handleUsernameChange(e.target.value)}
                                        error={!!fieldErrors.username}
                                        helperText={fieldErrors.username}
                                        required
                                        autoFocus
                                        disabled={isLoading}
                                        autoComplete="username"
                                        inputProps={{
                                            autoCapitalize: 'none',
                                            autoCorrect: 'off',
                                            spellCheck: false,
                                            'aria-required': true,
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <PersonIcon
                                                        sx={{
                                                            color: fieldErrors.username ? '#d32f2f' : '#0a6ed1',
                                                            fontSize: 20,
                                                        }}
                                                    />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: '#0a6ed1',
                                                    },
                                                },
                                            },
                                        }}
                                    />

                                    <TextField
                                        label="Password"
                                        type={showPassword ? 'text' : 'password'}
                                        variant="outlined"
                                        fullWidth
                                        size="small"
                                        margin="dense"
                                        value={password}
                                        onChange={(e) => handlePasswordChangeField(e.target.value)}
                                        error={!!fieldErrors.password}
                                        helperText={fieldErrors.password}
                                        required
                                        disabled={isLoading}
                                        autoComplete="current-password"
                                        inputProps={{
                                            'aria-required': true,
                                        }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        edge="end"
                                                        size="small"
                                                        disabled={isLoading}
                                                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                                                        aria-pressed={showPassword}
                                                        sx={{ mr: -0.5 }}
                                                    >
                                                        {showPassword ? (
                                                            <VisibilityOff
                                                                sx={{
                                                                    color: fieldErrors.password ? '#d32f2f' : '#6b7280',
                                                                    fontSize: 20,
                                                                }}
                                                            />
                                                        ) : (
                                                            <Visibility
                                                                sx={{
                                                                    color: fieldErrors.password ? '#d32f2f' : '#6b7280',
                                                                    fontSize: 20,
                                                                }}
                                                            />
                                                        )}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <LockIcon
                                                        sx={{
                                                            color: fieldErrors.password ? '#d32f2f' : '#0a6ed1',
                                                            fontSize: 20,
                                                        }}
                                                    />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: '#0a6ed1',
                                                    },
                                                },
                                            },
                                        }}
                                    />

                                    <Box
                                        sx={{
                                            mt: 1.5,
                                            mb: 0.5,
                                            display: 'flex',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <LoadCanvasTemplate />
                                    </Box>
                                    <TextField
                                        label="Enter Captcha"
                                        variant="outlined"
                                        fullWidth
                                        size="small"
                                        margin="dense"
                                        value={captchaInput}
                                        onChange={(e) => handleCaptchaChange(e.target.value)}
                                        error={!!fieldErrors.captcha}
                                        helperText={fieldErrors.captcha}
                                        required
                                        disabled={isLoading}
                                        autoComplete="off"
                                        inputProps={{
                                            autoCapitalize: 'none',
                                            autoCorrect: 'off',
                                            spellCheck: false,
                                            inputMode: 'text',
                                            'aria-required': true,
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: '#0a6ed1',
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                </Box>

                                <Button
                                    type="submit"
                                    variant="contained"
                                    fullWidth
                                    size="medium"
                                    endIcon={chosenApp ? <ArrowForwardIcon /> : undefined}
                                    sx={{
                                        mt: 2.5,
                                        mb: 1,
                                        bgcolor: '#0a6ed1',
                                        color: '#ffffff',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        py: 1.25,
                                        fontSize: '0.95rem',
                                        borderRadius: 1,
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            bgcolor: '#085bb0',
                                            transform: 'translateY(-1px)',
                                            boxShadow: '0 4px 12px rgba(10, 110, 209, 0.3)',
                                        },
                                        '&:active': {
                                            transform: 'translateY(0)',
                                        },
                                        '&:disabled': {
                                            bgcolor: '#b0bec5',
                                        },
                                    }}
                                    disabled={isLoading}
                                >
                                    {isLoading ? (
                                        <CircularProgress size={22} sx={{ color: '#ffffff' }} />
                                    ) : chosenApp ? (
                                        'Continue'
                                    ) : (
                                        'Sign In'
                                    )}
                                </Button>
                            </Box>

            <Typography
                variant="caption"
                align="center"
                display="block"
                sx={{
                    mt: 2,
                    color: 'text.secondary',
                    opacity: 0.7,
                    fontSize: '0.75rem',
                }}
            >
                Al-Dhow SAP PR System & Inbox © {new Date().getFullYear()}
            </Typography>
        </Paper>
    );

    return (
        <Fade in={true} timeout={reduceMotion ? 0 : 600}>
            <Box
                component="main"
                id="main-content"
                sx={{
                    minHeight: '100vh',
                    '@supports (min-height: 100svh)': { minHeight: '100svh' },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundImage: 'linear-gradient(135deg, #f5f7fa 0%, #e8edf3 100%)',
                    position: 'relative',
                    px: { xs: 2, sm: 3 },
                    py: { xs: 3, sm: 4 },
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'radial-gradient(circle at 10% 20%, rgba(10, 110, 209, 0.05) 0%, transparent 50%)',
                        pointerEvents: 'none',
                    },
                }}
            >
                <Container maxWidth="xs" sx={{ position: 'relative' }}>
                    {reduceMotion ? cardInner : (
                        <Slide direction="up" in={true} timeout={400} appear={true}>
                            {cardInner}
                        </Slide>
                    )}
                </Container>

                <ResponsiveDialog
                    open={showPasswordChangeDialog}
                    maxWidth="sm"
                    fullWidth
                    aria-labelledby="password-change-title"
                    PaperProps={{
                        sx: {
                            borderRadius: 2,
                            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                        },
                    }}
                >
                    <DialogTitle
                        id="password-change-title"
                        sx={{
                            bgcolor: '#0a6ed1',
                            color: '#ffffff',
                            py: 2,
                            px: 3,
                            fontWeight: 600,
                        }}
                    >
                        Change Password
                    </DialogTitle>
                    <DialogContent sx={{ pt: 3, pb: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            For security reasons, you must change your password before continuing.
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                            Your current password is the default password. Please choose a new, secure password.
                        </Typography>

                        {passwordChangeError && (
                            <Zoom in={true}>
                                <Box
                                    sx={{
                                        mb: 2,
                                        p: 1.5,
                                        borderRadius: 1,
                                        bgcolor: '#ffebee',
                                        border: '1px solid #ffcdd2',
                                        color: '#c62828',
                                        fontSize: '0.875rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    {passwordChangeError}
                                </Box>
                            </Zoom>
                        )}

                        <Grid container spacing={1.5}>
                            <Grid item xs={12}>
                                <TextField
                                    label="New Password"
                                    type="password"
                                    fullWidth
                                    size="small"
                                    value={newPassword}
                                    onChange={(e) => {
                                        setNewPassword(e.target.value);
                                        setPasswordChangeError(null);
                                    }}
                                    disabled={passwordChangeLoading}
                                    helperText="Min 8 chars, with uppercase, lowercase, and number"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 1,
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Confirm New Password"
                                    type="password"
                                    fullWidth
                                    size="small"
                                    value={confirmPassword}
                                    onChange={(e) => {
                                        setConfirmPassword(e.target.value);
                                        setPasswordChangeError(null);
                                    }}
                                    disabled={passwordChangeLoading}
                                    error={!!passwordChangeError && newPassword !== confirmPassword}
                                    helperText={passwordChangeError && newPassword !== confirmPassword ? 'Passwords do not match' : ''}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 1,
                                        },
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, py: 2 }}>
                        <Button
                            onClick={handlePasswordChange}
                            variant="contained"
                            color="primary"
                            disabled={passwordChangeLoading || !newPassword || !confirmPassword}
                            sx={{
                                bgcolor: '#0a6ed1',
                                '&:hover': {
                                    bgcolor: '#085bb0',
                                },
                            }}
                        >
                            {passwordChangeLoading ? (
                                <CircularProgress size={22} sx={{ color: '#ffffff' }} />
                            ) : (
                                'Change Password'
                            )}
                        </Button>
                    </DialogActions>
                </ResponsiveDialog>
            </Box>
        </Fade>
    );
};

export default Login;
